import { useEffect, useState } from "react";

import { setGlobalTheme } from "@atlaskit/tokens";
import { resetStyles } from "@atlassiansox/ads-theme-reset";

import { BoardBody } from "./board-body";
import { BoardHeader } from "./board-header";
import { SpotlightManager as Spotlight } from "./common";
import { SpotlightManagerProvider } from "./common/spotlight-provider";
import { CreateIssueModal, SuccessModal } from "./components";
import { modalBlanketStyle } from "./components/create-issue-modal.styles";
import { Message } from "./constants/types";
import {
    Assignee,
    EpicName,
    IssueTypeName,
    Priority,
    Steps
} from "./constants/ui";
import {
    jswInteractiveBoardDisplay,
    rootContainerStyle
} from "./jsw-interactive-board.styles";
import { useInteractiveBoardStore } from "./state";

export type JswInteractiveBoardProps = {
    messages: Message;
};

const JswInteractiveBoard = ({ messages }: JswInteractiveBoardProps) => {
    const [
        { isTicketModalOpen, step },
        { setMessages, setInProgressCol, setDoneCol }
    ] = useInteractiveBoardStore();

    useEffect(() => {
        void setGlobalTheme({ colorMode: "dark" });
    }, []);

    // Leveraging React Lazy Initialization to run setMessages, etc
    // only once at first render
    useState(() => {
        void setMessages(messages);

        void setInProgressCol([
            {
                id: "inProg1",
                summary: messages.inProgStaticLabel1,
                issueType: IssueTypeName.STORY,
                epicName: EpicName.FEATURES,
                assignee: Assignee.JANE,
                priority: Priority.BLOCKER
            },
            {
                id: "inProg2",
                summary: messages.inProgStaticLabel2,
                issueType: IssueTypeName.STORY,
                epicName: EpicName.FEATURES,
                assignee: Assignee.BLAKE,
                priority: Priority.HIGH
            }
        ]);
        void setDoneCol([
            {
                id: "done1",
                summary: messages.doneStaticLabel1,
                issueType: IssueTypeName.BUG,
                epicName: EpicName.FEATURES,
                assignee: Assignee.ALVIN,
                priority: Priority.MINOR
            },
            {
                id: "done2",
                summary: messages.doneStaticLabel2,
                issueType: IssueTypeName.TASK,
                epicName: EpicName.FEATURES,
                assignee: Assignee.JANE,
                priority: Priority.CRITICAL
            }
        ]);
    });

    return (
        <div css={jswInteractiveBoardDisplay}>
            <SpotlightManagerProvider>
                <div
                    css={[resetStyles, rootContainerStyle]}
                    data-testid="jsw-interactive-board"
                >
                    <BoardHeader />
                    <BoardBody />
                    {isTicketModalOpen && (
                        <div css={modalBlanketStyle}>
                            <CreateIssueModal />
                        </div>
                    )}
                    <SuccessModal />
                    {step === Steps.CREATE_FIRST_STORY && <Spotlight />}
                </div>
            </SpotlightManagerProvider>
        </div>
    );
};

export default JswInteractiveBoard;
